// Utility functions for pilot data processing and cost calculations

/**
 * Generate cost lookup key for a pilot (just the rarity)
 * @param {Object} pilot - Pilot from pilot_list.json with rarity
 * @returns {string} - Rarity string (rare, epic, legendary)
 */
export const generatePilotCostKey = (pilot) => {
  return pilot.rarity;
};

/**
 * Find cost data for a specific pilot star and level
 * @param {Array} costs - Array of cost data from pilot_upgrade_costs.json
 * @param {string} rarity - Pilot rarity (rare, epic, legendary)
 * @param {number} star - Star level (1-6)
 * @param {number} level - Level (1-7)
 * @returns {Object|null} - Cost object with acoins, marks, xp or null if not found
 */
export const findPilotCostData = (costs, rarity, star, level) => {
  const costEntry = costs.find(entry => 
    entry.star === star && entry.level === level
  );
  
  if (!costEntry) {
    return null;
  }
  
  return costEntry[rarity] || null;
};

/**
 * Calculate total upgrade cost from current to target position for a pilot
 * @param {Array} costs - Array of cost data from pilot_upgrade_costs.json
 * @param {string} rarity - Pilot rarity (rare, epic, legendary)
 * @param {number} currentStar - Current star level
 * @param {number} currentLevel - Current level
 * @param {number} targetStar - Target star level
 * @param {number} targetLevel - Target level
 * @returns {Object} - Total costs {acoins, marks, xp}
 */
export const calculatePilotUpgradeCost = (costs, rarity, currentStar, currentLevel, targetStar, targetLevel) => {
  let totalAcoins = 0;
  let totalMarks = 0;
  let totalXp = 0;
  
  let currentStarLevel = currentStar;
  let currentLevelLevel = (currentLevel - 1);
  targetLevel = targetLevel - 1;
  
  /**
   * Only if target level is 0, then go to last step
   */
  if(targetLevel == 0) {
    targetLevel = 7;
    targetStar = targetStar - 1;
  }
  
  // Progress through each upgrade step
  while (currentStarLevel < targetStar || (currentStarLevel === targetStar && currentLevelLevel < targetLevel)) {
    // Move to next level
    currentLevelLevel++;
    
    // If we exceed level 7, move to next star
    if (currentLevelLevel > 7) {
      currentStarLevel++;
      currentLevelLevel = 1;
    }
    
    // Get cost for this upgrade step
    const costData = findPilotCostData(costs, rarity, currentStarLevel, currentLevelLevel);
    
    if (costData) {
      totalAcoins += costData.acoins || 0;
      totalMarks += costData.marks || 0;
      totalXp += costData.xp || 0;
    }
    
    // Safety check to prevent infinite loops
    if (currentStarLevel > 6 || (currentStarLevel === 6 && currentLevelLevel > 7)) {
      break;
    }
  }
  
  return {
    acoins: totalAcoins,
    marks: totalMarks,
    xp: totalXp
  };
};

/**
 * Validate pilot upgrade parameters
 * @param {number} currentStar - Current star level
 * @param {number} currentLevel - Current level
 * @param {number} targetStar - Target star level
 * @param {number} targetLevel - Target level
 * @returns {Object} - {isValid: boolean, error: string}
 */
export const validatePilotUpgradeParams = (currentStar, currentLevel, targetStar, targetLevel) => {
  // Check if current position is valid
  if (currentStar < 1 || currentStar > 6 || currentLevel < 1 || currentLevel > 7) {
    return {
      isValid: false,
      error: 'Current position must be between 1-6 stars and 1-7 levels'
    };
  }
  
  // Check if target position is valid
  if (targetStar < 1 || targetStar > 6 || targetLevel < 1 || targetLevel > 7) {
    return {
      isValid: false,
      error: 'Target position must be between 1-6 stars and 1-7 levels'
    };
  }
  
  // Check if target is actually an upgrade
  if (targetStar < currentStar || (targetStar === currentStar && targetLevel <= currentLevel)) {
    return {
      isValid: false,
      error: 'Target position must be higher than current position'
    };
  }
  
  return { isValid: true, error: null };
};

/**
 * Calculate pilot upgrade steps with running totals
 * @param {Array} costs - Array of cost data from pilot_upgrade_costs.json
 * @param {string} rarity - Pilot rarity (rare, epic, legendary)
 * @param {number} currentStar - Current star level
 * @param {number} currentLevel - Current level
 * @param {number} targetStar - Target star level
 * @param {number} targetLevel - Target level
 * @returns {Array} - Array of upgrade steps with individual and running costs
 */
export const calculatePilotUpgradeSteps = (costs, rarity, currentStar, currentLevel, targetStar, targetLevel) => {
  const steps = [];
  let currentStarLevel = currentStar;
  let currentLevelLevel = (currentLevel - 1);
  
  // Running totals
  let runningAcoins = 0;
  let runningMarks = 0;
  let runningXp = 0;
  targetLevel = targetLevel - 1;
  
  /**
   * Only if target level is 0, then go to last step
   */
  if(targetLevel == 0) {
    targetLevel = 7;
    targetStar = targetStar - 1;
  }
  
  let toText;
  while (currentStarLevel < targetStar || (currentStarLevel === targetStar && currentLevelLevel < targetLevel)) {
    currentLevelLevel++;
    
    if (currentLevelLevel > 7) {
      currentStarLevel++;
      currentLevelLevel = 1;
    }
    
    const costData = findPilotCostData(costs, rarity, currentStarLevel, currentLevelLevel);
    toText = `${currentStarLevel}★ L${currentLevelLevel + 1}`;
    if ((currentLevelLevel + 1) == 8) {
      toText = `${currentStarLevel + 1}★ L1`;
    }
    
    if (costData) {
      // Add to running totals
      runningAcoins += costData.acoins || 0;
      runningMarks += costData.marks || 0;
      runningXp += costData.xp || 0;
      
      steps.push({
        from: `${currentStarLevel}★ L${currentLevelLevel}`,
        to: `${toText}`,
        acoins: costData.acoins || 0,
        marks: costData.marks || 0,
        xp: costData.xp || 0,
        // Running totals
        totalAcoins: runningAcoins,
        totalMarks: runningMarks,
        totalXp: runningXp
      });
    }
    
    // Safety check to prevent infinite loops
    if (currentStarLevel > 6 || (currentStarLevel === 6 && currentLevelLevel > 7)) {
      break;
    }
  }
  
  return steps;
};

/**
 * Format number with commas for display
 * @param {number} num - Number to format
 * @returns {string} - Formatted number
 */
export const formatNumber = (num) => {
  return num.toLocaleString();
};

